/**
 * A project file's History column (RFC 0313 3g, 3n): what git says happened to
 * it, beside the file, where a document shows its change graph.
 *
 * The commits that touched the path, newest first, and above them the change
 * not committed yet when the file is read from the working tree. No undo: a
 * commit is undone by another commit.
 */

import { GitBranch } from 'lucide-react'
import { CommitRow, LineCounts } from '../../services/code/CommitRow'
import { parseCodeId, worktreeBranch } from '../../services/code/codeFile'
import {
  lineCounts,
  useFileCommits,
  useWorkingFiles,
} from '../../services/code/codeHistory'

const time = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })

export function CodeFileHistory({
  projectId,
  codeId,
  onOpen,
}: {
  projectId: string
  codeId: string
  /** Open a file as it was at a commit. */
  onOpen?: (codeId: string) => void
}) {
  const parsed = parseCodeId(codeId)
  const sourceId = codeId.startsWith('code-source:')
    ? parsed?.sourceId
    : undefined
  // `~branch` is that branch's worktree; its log is the branch's. A plain ref
  // is read as itself; none is the checkout's HEAD.
  const branch = worktreeBranch(parsed?.ref ?? null)
  const ref = branch ?? parsed?.ref ?? undefined
  const commits = useFileCommits(projectId, parsed?.path, ref, sourceId)
  // Uncommitted work is asked of the main checkout only, so only when the file
  // is read from its working tree.
  const working = useWorkingFiles(
    parsed && !parsed.ref ? projectId : undefined,
    sourceId,
  )
  const mine = working.filter((f) => f.path === parsed?.path)

  if (!parsed) return null
  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto pb-4">
      {mine.length ? (
        <div className="mx-2 mt-2 rounded-lg border border-border/70 bg-card px-2.5 py-2">
          <div className="flex items-center gap-2 text-xs">
            <GitBranch className="size-3.5 shrink-0 text-muted-foreground" />
            <span className="text-[13px] text-foreground">Not committed</span>
            <span className="ml-auto text-[11px]">
              <LineCounts {...lineCounts(mine)} />
            </span>
          </div>
        </div>
      ) : null}
      {commits.map((c) => (
        <div
          key={c.sha}
          className="mx-2 mt-2 rounded-lg border border-border/70 bg-card px-2.5 py-2"
        >
          <CommitRow
            projectId={projectId}
            sourceId={sourceId}
            commit={c}
            byline={`${c.author} · ${time(c.date)}`}
            {...(onOpen ? { onOpen } : {})}
          />
        </div>
      ))}
      {!mine.length && !commits.length ? (
        <p className="px-3 py-3 text-muted-foreground text-xs">
          No history in git for this file.
        </p>
      ) : null}
    </div>
  )
}
